<template>
	<div class="advert-manage-container">
		<el-button-group>
			<el-button
				v-for="tab in $menu.getList('RentType')"
				:key="tab.value"
				:type="rentType === tab.value ? 'primary' : ''"
				size="small"
				@click="tabClick(tab)">
				{{ tab.name }}
			</el-button>
		</el-button-group>
		<el-row :gutter="20">
			<el-col :span="24" class="rows">
				<el-card shadow="never">
					<div class="header-row" slot="header">
						<div>{{ $menu.getLabel('RentType', rentType) }}租赁统计</div>
						<el-date-picker
							v-model="month"
							type="month"
							placeholder="选择日期"
							size="small"
							value-format="yyyy-MM"
							@change="handerChange"></el-date-picker>
					</div>
					<div class="flex-wrap" v-loading="loading">
						<div class="flex-item" v-for="item in areaList" :key="item.title">
							<div class="flex-content">
								<svg-icon :name="item.icon" :size="42" />
								<div>
									<div class="title">{{ item.title }}</div>
									<div class="value" :style="{ color: item.color }">
										{{ formatNumber(item.value) }}
										<span class="unit">{{ item.unit }}</span>
									</div>
								</div>
							</div>
							<div class="tip" v-if="item.info">
								<el-tooltip effect="dark" :content="item.info" placement="top">
									<i class="fa el-icon-info" style="color: #938e8e; font-size: 14px"></i>
								</el-tooltip>
							</div>
						</div>
					</div>
				</el-card>
			</el-col>
			<el-col :span="24" class="rows">
				<el-card shadow="never">
					<div class="header-row" slot="header">月出租趋势</div>
					<div class="line-chart">
						<BarLineChart :xData="xData" :barData="barData" :lineData="lineData" />
					</div>
				</el-card>
			</el-col>
			<el-col :span="12" class="rows">
				<el-card shadow="never">
					<div class="header-row" slot="header">合同到期分布</div>
					<div class="pie-chart">
						<PieChart :categories="expiryDistribution" />
					</div>
				</el-card>
			</el-col>
			<el-col :span="12" class="rows">
				<el-card shadow="never">
					<div class="header-row" slot="header">合同租期分布</div>
					<div class="pie-chart">
						<PieChart :categories="termDistribution" />
					</div>
				</el-card>
			</el-col>
		</el-row>
	</div>
</template>

<script>
	import dayjs from 'dayjs';
	import {
		getRentalOverview,
		getRentalLast6months,
		getRentalContractTerm,
		getRentalContractExpiry,
	} from '@/api/assets/leasingAnalysisApi.js';
	import { getCommunityId } from '@/utils/vc.js';
	export default {
		name: 'LeasingAnalysis',
		data() {
			return {
				month: dayjs().format('YYYY-MM'),
				rentType: 'HOUSE',
				loading: false,
				areaList: [
					{
						key: 'totalBuildingArea',
						title: '总面积',
						value: 0,
						unit: '㎡',
						icon: 'area',
						info: null,
					},
					{ key: 'rentableArea', title: '可出租面积', value: 0, unit: '㎡', icon: 'lettable_area', info: null },
					{
						key: 'rentalRate',
						title: '出租率',
						value: 0,
						unit: '%',
						icon: 'letting_rate',
						info: '出租率 = 当日已出租面积 / 可出租面积 × 100%',
					},
					{
						key: 'vacancyRate',
						title: '空置率',
						value: 0,
						unit: '%',
						icon: 'vacancy_rate',
						info: '空置率 = 当日空置面积 / 可出租面积 × 100%',
					},
					{ key: 'rentedArea', title: '已出租面积', value: 0, unit: '㎡', icon: 'Rented_out', info: null },
					{ key: 'unrentedArea', title: '未出租面积', value: 0, unit: '㎡', icon: 'not_rented', info: null },

					{
						key: 'targetRentalRate',
						title: '本月计租率',
						value: 0,
						unit: '%',
						icon: 'rental_rate',
						info: '本月计租率 = Σ（本月单个已出租资源面积 × 本月单个资源出租天数）/（本月可出租面积 × 可出租天数）× 100%',
					},
					{
						key: 'averageMonthlyRent',
						title: '本月均价',
						value: 0,
						unit: '元/㎡/月',
						icon: 'average_price',
						info: '本月均价 = 本月租金收费项目应收金额合计 / 已出租面积',
					},
				],
				expiryDistribution: [],
				termDistribution: [],
				xData: [],
				barData: [],
				lineData: [],
			};
		},
		created() {
			this.fetchGetRentalOverview();
			this.fetchGetRentalLast6months();
			this.fetchGetRentalContractTerm();
			this.fetchGetRentalContractExpiry();
		},
		methods: {
			async fetchGetRentalOverview() {
				try {
					this.loading = true;
					const res = await getRentalOverview({
						rentType: this.rentType,
						month: this.month,
						communityId: getCommunityId(),
					});
					this.areaList = this.areaList.map(item => ({
						...item,
						value: res ? res[item.key] || 0 : 0,
					}));
				} catch (error) {
					console.log(error);
				} finally {
					this.loading = false;
				}
			},
			async fetchGetRentalLast6months() {
				try {
					const res = await getRentalLast6months({ rentType: this.rentType, communityId: getCommunityId() });
					this.xData = res.map(item => item.statisticsMonth);
					this.barData = res.map(item => item.rentedArea);
					this.lineData = res.map(item => item.rentalRate);
				} catch (error) {
					console.log(error);
				}
			},
			async fetchGetRentalContractTerm() {
				try {
					const res = await getRentalContractTerm({ rentType: this.rentType, communityId: getCommunityId() });
					this.termDistribution = res.map(item => ({
						name: item.contractTermType,
						value: item.count,
					}));
				} catch (error) {
					console.log(error);
				}
			},
			async fetchGetRentalContractExpiry() {
				try {
					const res = await getRentalContractExpiry({ rentType: this.rentType, communityId: getCommunityId() });
					this.expiryDistribution = res.map(item => ({
						name: item.year,
						value: item.count,
					}));
				} catch (error) {
					console.log(error);
				}
			},
			formatNumber(num) {
				return num.toLocaleString();
			},
			handerChange(e) {
				this.fetchGetRentalOverview();
			},
			dayjs,
			tabClick(tab) {
				this.rentType = tab.value;
				this.fetchGetRentalOverview();
				this.fetchGetRentalLast6months();
				this.fetchGetRentalContractTerm();
				this.fetchGetRentalContractExpiry();
			},
		},
	};
</script>

<style lang="scss" scoped>
	.advert-manage-container {
		padding: 20px;
		background-color: #f9fafc;
		text-align: left;

		:deep(.el-button-group) {
			padding: 5px;
			background: rgba(64, 128, 255, 0.1);
			box-shadow: 0px 1 2px 0px rgba(0, 0, 0, 0.05);
			border-radius: 3px;
			.el-button {
				border: none;
				background-color: transparent;
				color: #414755;
				border-radius: 3px !important;
				padding: 6px 18px;
				transition: all 0.3s;
			}
			.el-button--primary {
				background-color: #fff;
				color: #4080ff;
			}
		}

		:deep(.el-card) {
			border-radius: 16px;
			.el-card__header {
				border-bottom: none;
				padding-bottom: 0;
			}
		}

		.rows {
			margin-top: 14px;
		}

		.header-row {
			display: flex;
			justify-content: space-between;
			align-items: center;
			color: #0b1c30;
			font-weight: 600;
			font-size: 16px;
		}

		.line-chart {
			width: 100%;
			height: 352px;
		}

		.pie-chart {
			width: 100%;
			height: 300px;
		}

		.tip {
			cursor: help;
		}

		.flex-wrap {
			display: flex;
			flex-wrap: wrap;
			gap: 16px;
		}

		.flex-item {
			width: calc(25% - 16px);
			background: #f9fafc;
			box-sizing: border-box;
			padding: 16px;
			border-radius: 8px;
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		.flex-content {
			display: flex;
			align-items: center;
			gap: 12px;
		}

		.title {
			color: #1c1c1c;
			font-weight: 500;
			font-size: 14px;
		}

		.value,
		.unit {
			font-weight: 600;
			font-size: 21px;
			color: #1c1c1c;
			margin-top: 3px;
		}

		.unit {
			font-size: 14px;
		}
	}
</style>
